import { useEffect, useState } from 'react'

import { Grid } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { doc, getDoc, updateDoc } from 'firebase/firestore'

import MDBox from 'components/MDBox'
import { BASE_COLLECTION, db } from 'config/firebase'
import { useMaterialUIController } from 'context'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import ManageCard from 'modules/ManageCard'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { FirebaseOrganisationData } from 'types/firebase'

import MainForm from './components/MainForm'
import { AmbiguousLocationsFormContext } from './constants'
import { AmbiguousLocations } from './type'

function ManageAmbiguousLocations() {
	const [controller] = useMaterialUIController()
	const { session } = controller

	const [ambiguousLocationsForm, setAmbiguousLocationsForm] = useState<AmbiguousLocations[]>([])
	const [formEdited, setFormEdited] = useState<boolean>(false)
	const [showError, setShowError] = useState<boolean>(false)

	const fetchData = async () => {
		const docRef = doc(db, `${BASE_COLLECTION}`, session.data.organisation.id)
		const docSnap = await getDoc(docRef)
		return (docSnap.data() as FirebaseOrganisationData).ambiguous_locations
	}

	const updateAmbiguousLocations = async () => {
		try {
			const docRef = doc(db, `${BASE_COLLECTION}`, session.data.organisation.id)
			const cleanedLocations = ambiguousLocationsForm
				.map(loc => {
					const trimmedName = loc.name?.trim() || ''
					const cleanedSuggestions = (loc.suggestions || [])
						.map(s => s.trim())
						.filter(s => s !== '')

					// Start with the base object
					const cleanedLoc = {
						...loc,
						name: trimmedName,
					}

					// If suggestions exist, add them.
					// If they don't, we DON'T add the key at all.
					if (cleanedSuggestions.length > 0) {
						cleanedLoc.suggestions = cleanedSuggestions
					} else {
						// Remove the key if it exists from the state object
						delete cleanedLoc.suggestions
					}

					return cleanedLoc
				})
				.filter(loc => loc.name !== '')
			await updateDoc(docRef, {
				ambiguous_locations: cleanedLocations,
			})
			return true
		} catch {
			return false
		}
	}

	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ['ambiguous-locations'],
		queryFn: fetchData,
	})

	useEffect(() => {
		setAmbiguousLocationsForm(data || [])
	}, [data])

	return (
		<AmbiguousLocationsFormContext.Provider
			value={{
				ambiguousLocationsForm,
				setAmbiguousLocationsForm,
				formEdited,
				setFormEdited,
				showError,
				setShowError,
			}}
		>
			<DashboardLayout>
				<DashboardNavbar isMini />
				<MDBox mt={4}>
					<Grid container spacing={3}>
						<Grid item xs={12} lg={9}>
							<MDBox mb={3}>
								{!isLoading && !error ? <MainForm /> : <LoadingComponent type="box" />}
							</MDBox>
						</Grid>
						<Grid item xs={12} lg={3}>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<ManageCard
										formEdited={formEdited}
										setFormEdited={setFormEdited}
										save={{
											onClicked: async () => {
												const success = await updateAmbiguousLocations()
												refetch()
												if (!success) {
													throw new Error('Error Uploading')
												}
											},
										}}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</MDBox>
			</DashboardLayout>
		</AmbiguousLocationsFormContext.Provider>
	)
}

export default ManageAmbiguousLocations
